<template>
  <div class="overview-container">
    <div class="oc-top-container">
      <WatchlistButton v-if="isSmacAdmin" :customerId="customer.id" />
      <button
        v-if="isSmacAdmin && customer.hubspotId"
        type="button"
        class="overview-exernal-button"
        @click="navToSite('hubspot')"
      >
        <img
          src="@shared/assets/img/general/hubspot.png"
          class="img-size-28"
          alt="hubspot"
        />
      </button>
      <button
        v-if="isSmacAdmin && customer.chargebeeId"
        type="button"
        class="overview-exernal-button"
        @click="navToSite('chargebee')"
      >
        <img
          src="@shared/assets/img/general/chargebee.png"
          class="img-size-28"
          alt="chargebee"
        />
      </button>
      <SACSettingMenu :options="settingMenu" />
    </div>
    <div
      v-if="customer.membershipStatus && customer.membershipStatus !== 'Active'"
      class="oc-row-container margin-top-4"
    >
      <div class="bc-error-container">
        <img alt="red-circle" src="@shared/assets/img/general/i-red-circle.svg" />
        <div class="bc-error-info-container">
          <div v-if="membershipRemaining" class="bc-error-info-title">
            {{ cancellationStatus }} - {{ membershipRemaining }}
            {{ membershipRemaining > 1 ? "months" : "month" }}
            remaining
          </div>
          <div v-else class="bc-error-info-title">
            {{ cancellationStatus }}
          </div>
          <div class="bc-error-info-sub">
            This member has been canceled since {{ getFullYear(cancelDate) }}
            <div class="bc-error-sub-info">
              {{ cancelReason }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <VirtualVisits
      v-if="
        !processing && isLoomSupported && virtualVisits.length > 0 && !recordingInProgress
      "
    />
    <FirstVirtualVisit
      v-if="
        !processing &&
        adminRole !== 'viewer' &&
        isLoomSupported &&
        providerPlan?.planType === 'Hardware' &&
        virtualVisits.length === 0 &&
        !recordingInProgress
      "
    />
    <div class="oc-row-container">
      <ContactInfo />
      <SensorDetail v-if="isMonitored" />
      <SmartThermostatDetail v-else />
    </div>
    <div v-if="isMonitored" class="oc-row-container">
      <HubDetail />
    </div>
    <div class="oc-row-container">
      <FilterDetail />
    </div>
    <div v-if="isSmacAdmin && isMonitored" class="oc-row-container">
      <ThermostatDetail />
    </div>
    <div v-if="isSmacAdmin && chronicAlerts.length" class="oc-row-container">
      <ChronicDetail />
    </div>
    <div class="oc-row-container">
      <HVACDetail />
      <HomeDetail />
    </div>
  </div>
</template>

<script>
import customerDetailMixin from "@shared/mixins/customerDetailMixin";
import { isSupported } from "@loomhq/record-sdk";

import SACSettingMenu from "@shared/components/sac/SACSettingMenu.vue";
import ContactInfo from "./ContactInfo.vue";
import HomeDetail from "./HomeDetail.vue";
import HVACDetail from "./HVACDetail.vue";
import SmartThermostatDetail from "./SmartThermostatDetail.vue";
import SensorDetail from "./SensorDetail.vue";
import ThermostatDetail from "./ThermostatDetail.vue";
import FilterDetail from "./FilterDetail.vue";
import ChronicDetail from "./ChronicDetail.vue";
import HubDetail from "./HubDetail.vue";
import FirstVirtualVisit from "./FirstVirtualVisit.vue";
import VirtualVisits from "./VirtualVisits.vue";
import WatchlistButton from "../shared/WatchlistButton.vue";

export default {
  name: "OverviewView",
  components: {
    SACSettingMenu,
    ContactInfo,
    HomeDetail,
    HVACDetail,
    SensorDetail,
    ThermostatDetail,
    SmartThermostatDetail,
    FilterDetail,
    ChronicDetail,
    HubDetail,
    FirstVirtualVisit,
    VirtualVisits,
    WatchlistButton,
  },
  mixins: [customerDetailMixin],
  data() {
    return {
      isLoomSupported: false,
      processing: false,
    };
  },
  computed: {
    cancellationStatus() {
      if (this.customer.membershipStatus === "Pending Cancellation") {
        return "Cancellation Pending";
      }
      return "Membership Canceled";
    },
    membershipRemaining() {
      if (
        this.customer.membershipStatus === "Canceled" ||
        this.customer.contractEndDate == null
      ) {
        return null;
      }
      const contractEndDate = new Date(this.customer.contractEndDate);
      const currentDate = new Date();

      const monthsRemaining = Math.ceil(
        (contractEndDate.getTime() - currentDate.getTime()) /
          (1000 * 60 * 60 * 24 * 30.44)
      );
      return Math.max(0, monthsRemaining);
    },
    settingMenu() {
      const menuOptions = [
        {
          label: "See Monthly Report",
          class: "blue-btn",
          action: () => this.navToMHR(),
        },
      ];
      if (this.isSmacAdmin) {
        menuOptions.push(
          {
            label: "Merge Member",
            action: () => this.toggleActionModal(true, "merge-member"),
            class: "common-btn",
          },
          {
            label: "Send Welcome Email",
            action: () => this.toggleActionModal(true, "welcome-email"),
            class: "common-btn",
          },
          {
            label: "Place an Order",
            action: () => this.toggleOrderActionModal(true, "new-order"),
            class: "common-btn",
          },
          {
            label: "Adjust Rewards",
            action: () => this.toggleActionModal(true, "adjust-rewards"),
            class: "common-btn",
          },
          {
            label: "Sync With HubSpot",
            action: () => this.toggleActionModal(true, "sync-hubspot"),
            class: "common-btn",
          }
        );
      }

      if (this.customer.membershipStatus === "Active" && this.adminRole !== "viewer") {
        menuOptions.push({
          label: "Cancel Member",
          class: "red-btn",
          action: () => this.toggleActionModal(true, "deactivate-member"),
        });
      } else if (this.adminRole !== "viewer") {
        menuOptions.push({
          label: "Activate Member",
          class: "blue-btn",
          action: () => this.toggleActionModal(true, "activate-member"),
        });
      }
      return menuOptions;
    },
    isMonitored() {
      return this.customer?.membershipType === "Monitored";
    },
    cancelReason() {
      let reason = "No reason";
      if (this.customer.cancelReason) {
        reason = this.customer.cancelReason;
      } else {
        this.customer.memberships?.forEach((membership) => {
          if (membership.status?.toLowerCase() !== "active" && membership.cancelReason) {
            reason = membership.cancelReason;
          }
        });
      }
      return reason;
    },
    cancelDate() {
      if (this.customer.cancelDate) {
        return this.customer.cancelDate;
      }
      let date = null;
      this.customer.memberships?.forEach((membership) => {
        if (membership.status?.toLowerCase() !== "active") {
          if (membership.cancelDate) {
            date = membership.cancelDate;
          } else if (membership.endDate) {
            date = membership.endDate;
          } else {
            date = membership.creationDate;
          }
        }
      });
      return date;
    },
  },
  async mounted() {
    this.processing = true;
    const { id: customerId } = this.customer;
    const { supported, error } = await isSupported();
    if (!supported) {
      console.error(`Error setting up Loom: ${error}`);
    }
    this.isLoomSupported = supported;
    if (this.isLoomSupported && customerId) {
      await this.$store.dispatch("getVirtualVisits");
    }

    this.processing = false;
  },
  methods: {
    navToSite(type) {
      let urlNav;
      if (type === "chargebee") {
        urlNav = `https://smartac.chargebee.com/d/customers/${this.customer.chargebeeId}`;
      } else {
        urlNav = `https://app.hubspot.com/contacts/8339855/contact/${this.customer.hubspotId}`;
      }
      window.open(urlNav, "_blank");
    },
    async navToMHR() {
      const token = await this.$store.dispatch("getRedirectCode");
      const { id: customerId } = this.customer;
      const urlNav = `${process.env.MEMBER_PORTAL_URL}/mhr?email=${localStorage.loginEmail}&token=${token.data}&customerId=${customerId}`;
      window.open(urlNav, "_blank");
    },
  },
};
</script>

<style lang="scss">
.overview-container {
  max-width: 1705px;
  width: 100%;

  .overview-exernal-button {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: $WHITE;
    width: 40px;
    height: 40px;
    border-radius: 6px;
    margin-left: 10px;
    cursor: pointer;

    &:hover {
      background-color: $N20;
    }
  }

  .oc-mr-button {
    padding: 12px 16px;
    background: $NBLUE100;
    border-radius: 6px;
    color: $WHITE;
    font-size: 1rem;
    font-weight: 700;
  }

  .oc-cancel-btn {
    padding: 12px 16px;
    background: $TOMATO100;
    border-radius: 6px;
    color: $WHITE;
    font-size: 1rem;
    font-weight: 700;
  }

  .b-row-container {
    display: flex;
    justify-content: space-between;
    margin-top: 24px;

    .b-card-container {
      &:last-child {
        margin-left: 24px;
      }
    }

    .bi-body {
      margin-top: 12px;
      font-size: 1rem;
      color: $N100;
    }
  }

  .oc-top-container {
    display: flex;
    justify-content: flex-end;

    .setting-menu-container {
      width: auto;
    }
  }

  .oc-row-container {
    display: flex;
    justify-content: space-between;
  }
  .b-card-container {
    width: 100%;
    max-height: 340px;
    overflow: auto;
    border-radius: 8px;
    background-color: $WHITE;
    margin-top: 24px;
    position: relative;
    padding-bottom: 16px;
    &:not(:first-child) {
      margin-left: 12px;
    }

    &.flex-1 {
      flex: 1;
    }

    &.flex-2 {
      flex: 2;
      max-height: unset;
    }

    .processing-overlay {
      position: absolute;
      height: 100%;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      background-color: #b8bcc7d1;
      color: $WHITE;
      font-size: 1rem;
      font-weight: 500;
    }

    .bc-header-container {
      display: flex;
      align-items: center;
      justify-content: space-between;
      position: sticky;
      padding: 0 16px;
      height: 50px;
      z-index: 2;
      top: 0;
      background-color: $CONTENTGRAY;

      .bc-button-container {
        display: flex;

        .cancel-btn {
          color: $WHITE;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          margin-right: 12px;
        }

        .add-btn {
          color: $WHITE;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          margin-right: 12px;
          outline: none;
        }

        .edit-btn {
          color: $WHITE;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          border: 1px solid;
          border-radius: 5px;
          padding: 0 10px;
        }

        .welcome-email-btn {
          color: $BLACK;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          border: 1px solid;
          border-radius: 5px;
          padding: 0 5px;
        }
      }
    }

    .bc-header {
      font-size: 1.125rem;
      color: $WHITE;
      font-weight: 500;

      @media (max-width: 1440px) {
        font-size: 1rem;
      }
    }

    .bc-line-container {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 12px;

      .bl-header {
        font-size: 1rem;
        font-weight: 500;
        color: $N100;

        &:first-child {
          width: 40%;
        }

        &:nth-child(2) {
          width: 10%;
          text-align: center;
        }

        &:nth-child(3) {
          width: 10%;
          text-align: center;
        }

        &:nth-child(4) {
          width: 10%;
          text-align: center;
        }

        &:nth-child(5) {
          width: 30%;
          text-align: right;
        }
      }

      .bl-body {
        font-size: 1rem;
        color: $N100;

        &.input-container {
          margin-top: 0;
        }

        &:first-child {
          width: 40%;
        }

        &:nth-child(2) {
          width: 10%;
          text-align: center;
        }

        &:nth-child(3) {
          width: 10%;
          text-align: center;
        }

        &:nth-child(4) {
          width: 10%;
          text-align: center;
        }

        &:nth-child(5) {
          width: 30%;
          text-align: right;
        }
      }
    }

    .bo-row-container {
      display: flex;
      justify-content: space-between;
      margin-top: 12px;

      .bo-body-container {
        display: flex;
        flex-direction: column;

        &.center {
          align-items: center;
        }

        &:last-child {
          align-items: flex-end;
        }

        .bo-header {
          font-size: 1rem;
          color: $N100;
          font-weight: 500;

          @media (max-width: 1440px) {
            font-size: 0.875rem;
          }
        }

        .input-container {
          margin-top: 0;
        }
      }
    }

    .input-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .input-container {
      display: flex;
      flex-direction: column;
      margin-top: 12px;
      width: 100%;

      &.two {
        width: 49%;
      }

      &.three {
        width: 32%;
      }

      input,
      select {
        height: 48px;
        border: 1px solid $MEDIUMGRAY;
        border-radius: 8px;
        padding-left: 12px;
        width: 100%;

        &::placeholder {
          color: $N30;
        }

        &.placeholder {
          color: $N30;
        }
      }
    }

    .i-error {
      font-size: 12px;
      color: $TOMATO100;
    }

    .bi-body-container {
      display: flex;
      flex-direction: column;
      padding: 10px;

      .bi-row-container {
        display: flex;
        justify-content: space-between;
      }

      .bi-column-container {
        display: flex;
        flex-direction: column;
        width: 48%;
      }
    }

    .bc-row-container {
      display: flex;
      justify-content: space-between;

      &.margin-top {
        margin-top: 16px;
      }

      .bc-column-container {
        display: flex;
        flex-direction: column;
        width: 100%;
      }

      .bc-column-container {
        display: flex;
        flex-direction: column;
        width: 100%;
      }

      .bc-sub-row-container {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        width: 100%;
        margin-top: 12px;
        padding: 0 16px;
      }

      .bc-info-container {
        display: flex;
        margin-top: 12px;
        align-items: flex-start;
        justify-content: space-between;
        padding: 0 16px;

        .bc-info-input {
          height: 48px;
          border: 1px solid $NBLUE10;
          border-radius: 8px;
          padding-left: 12px;
          width: 50%;

          &::placeholder {
            color: $N30;
          }

          &.placeholder {
            color: $N30;
          }
        }
      }

      .bi-body {
        font-size: 1rem;
        color: $N100;
        text-align: right;

        @media (max-width: 1440px) {
          font-size: 0.875rem;
        }
      }
    }
  }

  .bc-error-container {
    align-items: center;
    background-color: #ef5b5229;
    border: 2px solid;
    border-color: #ee5a52;
    border-radius: 4px;
    display: flex;
    padding: 10px 24px;
    width: 100%;
    align-items: center;

    .bc-error-info-container {
      display: flex;
      flex-direction: column;
      margin-left: 10px;

      .bc-error-info-title {
        font-size: 18px;
        font-weight: 600;
      }

      .bc-error-info-sub {
        display: flex;
        align-items: center;
        background-color: #ffffff00;
        font-weight: 600;
        color: #000000;

        .bc-error-sub-info {
          border: 2px solid;
          border-color: #ee5a52;
          border-radius: 4px;
          padding: 3px 8px;
          color: #000000;
          font-style: normal;
          font-weight: 600;
          margin: 0 8px;
        }

        p {
          color: #000000;
          font-size: 14px;
          font-weight: 500;
        }
      }
    }
  }

  .bc-date-container {
    width: 50%;
  }

  .bc-image-container {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;

    .hvac-image {
      height: 100px;
      width: auto;
    }

    .no-image {
      font-size: 18px;
      font-weight: 500;
    }
  }

  .bcb-button-container {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 16px;

    .bcb-button {
      width: 180px;
      height: 40px;
      border-radius: 6px;
      color: $WHITE;
      background-color: $NBLUE100;
      font-weight: 600;
      padding: 0 8px;
    }

    .bcb-s-button {
      width: 180px;
      height: 48px;
      border-radius: 6px;
      color: $WHITE;
      background-color: $N80;
      font-size: 16px;
      font-weight: 500;
      margin-right: 16px;
    }
  }

  .bc-table-container {
    display: flex;
    flex-direction: column;
    margin-top: 12px;
    padding: 0 16px;

    .bct-header-container {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 12px;

      .bct-header {
        flex: 1;
        font-size: 14px;
        font-weight: 500;
        text-align: center;

        @media (max-width: 1440px) {
          font-size: 0.875rem;
        }

        &:first-child {
          text-align: left;
        }

        &:last-child {
          text-align: right;
        }
      }

      .bct-icon-header {
        width: 40px;
      }
    }

    .bct-body-container {
      display: flex;
      flex-direction: column;

      .bct-row-container {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 12px;

        .bct-body {
          flex: 1;
          font-size: 14px;
          text-transform: capitalize;
          position: relative;
          text-align: center;

          @media (max-width: 1440px) {
            font-size: 0.875rem;
          }

          &:first-child {
            text-align: left;
          }

          &:last-child {
            text-align: center;
          }

          .bct-tooltip {
            display: none;
            position: absolute;
            color: $BLACK;
            top: -60px;
            left: -60px;
            width: 220px;
            background: $WHITE;
            box-shadow: 0px 3px 6px rgba(9, 15, 32, 0.07);
            z-index: 2;
            padding: 16px 24px;
          }

          &:hover {
            .bct-tooltip {
              display: block;
            }
          }
        }

        .bct-icon-container {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;

          &:last-child {
            justify-content: flex-end;
          }
        }

        .editable {
          cursor: pointer;
        }

        .bct-sub-row-container {
          display: grid;
          grid-auto-flow: column;
          justify-content: flex-end;
          margin-bottom: 5px;
        }

        .bct-input {
          border: 1px solid $N20;
          height: 30px;
          border-radius: 6px;
          padding: 0 5px;
          width: 120px;
        }
      }
    }
  }

  .chronic-table-container {
    display: flex;
    flex-direction: column;
    margin-top: 12px;

    .chronic-header-container {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 10px;
      padding: 5px 20px;

      .chronic-header {
        font-size: 1rem;
        font-weight: 500;

        @media (max-width: 1440px) {
          font-size: 0.875rem;
        }

        width: 12%;

        &:nth-child(2) {
          width: 15%;
        }

        &:nth-child(3) {
          text-align: center;
        }

        &:nth-child(4) {
          text-align: center;
        }

        &:nth-child(5) {
          width: 40%;
          text-align: left;
        }

        &:last-child {
          width: 3%;
          text-align: right;
        }
      }
    }

    .chronic-body-container {
      display: flex;
      flex-direction: column;
      padding: 5px 20px;

      &:nth-child(even) {
        background-color: $N10;
      }

      .chronic-row-container {
        display: flex;
        align-items: center;
        justify-content: space-between;

        .chronic-body {
          @media (max-width: 1440px) {
            font-size: 0.875rem;
          }

          width: 12%;

          &:nth-child(2) {
            width: 15%;
          }

          &:nth-child(3) {
            text-align: center;
          }

          &:nth-child(4) {
            text-align: center;
          }

          &:nth-child(5) {
            width: 40%;
            text-align: left;
            font-size: 14px;
          }

          &:last-child {
            width: 3%;
            text-align: right;
          }

          .chronic-button {
            vertical-align: middle;
          }
        }
      }
    }
  }
}
</style>
